import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Collapse,
  Badge
} from '@mui/material';
import { 
  Menu as MenuIcon,
  AccountCircle,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ContentCut as ContentCutIcon,
  Assignment as AssignmentIcon,
  Payments as PaymentsIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Logout as LogoutIcon,
  Checkroom as CheckroomIcon,
  Cached as CachedIcon,
  BusinessCenter as BusinessIcon,
  Work as WorkIcon,
  DesignServices as DesignIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Engineering as EngineeringIcon,
  Timeline as TimelineIcon,
  RateReview as RateReviewIcon,
  Analytics as AnalyticsIcon,
  ExpandLess,
  ExpandMore,
  Factory as FactoryIcon,
  PlaylistAdd as PlaylistAddIcon,
  Archive as ArchiveIcon,
  Build as BuildIcon,
  Iron as IronIcon,
  CheckCircle as CheckCircleIcon,
  ChatBubbleOutline as ChatIcon,
  Assessment as AssessmentIcon,
  VerifiedUser as QcIcon,
  Storefront as StorefrontIcon,
  ManageSearch as AuditIcon,
  SpaceDashboard as SpaceDashboardIcon,
  AdminPanelSettings as RolesIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import api from '../../services/api';
import { isPwaMode } from '../../utils/pwaDetection';
import ClearCacheButton from '../ClearCacheButton';
import { clearAppCache, forceRefreshApp } from '../../utils/cacheManager';
import NotificationBell from '../NotificationBell';
import { messagingApi } from '../../api/messaging';

const drawerWidth = 240;

interface MainLayoutProps {
  children: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clearCacheDialogOpen, setClearCacheDialogOpen] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [isPwaActive, setIsPwaActive] = useState(false);
  const { user: authUser, logout } = useAuth();
  const { user: permUser, hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [companyData, setCompanyData] = useState({
    name: '',
    brand_name: '',
    address: '',
    contact_number: '',
    email: ''
  });
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companySuccess, setCompanySuccess] = useState<string | null>(null);
  // Messaging unread count for Chat nav badge
  const [messagingUnread, setMessagingUnread] = useState(0);

  useEffect(() => {
    if (!authUser) return;
    const fetch = () => {
      messagingApi.getUnreadCount().then(d => setMessagingUnread(d.unread_count || 0)).catch(() => {});
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [authUser]);

  // Analytics menu removed
  const [productionOpen, setProductionOpen] = useState(false);
  const [deprecatedOpen, setDeprecatedOpen] = useState(false);
  const [stitchingUnitOpen, setStitchingUnitOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  
  // Add debugging on component mount
  useEffect(() => {
    if (authUser && authUser.email === 'santosh@peachmode.com') {
      console.log('%c MainLayout Debug for Santosh User', 'background: #2196F3; color: white; padding: 4px; border-radius: 4px;');
      console.log('User in MainLayout:', { 
        id: authUser.id,
        name: authUser.name, 
        email: authUser.email,
        role: authUser.role,
        permissions: authUser.permissions 
      });
      
      // Debug test permissions for each menu module
      const testModules = ['workers', 'design-cutting', 'assignments', 'payments', 'dispatch', 'expenses', 'users'];
      console.log('MainLayout direct permission tests:');
      testModules.forEach(module => {
        const hasViewAccess = hasAnyPermission(module);
        console.log(`${module}: canView returns ${hasViewAccess ? '✅ true' : '❌ false'}`);
      });
    }
  }, [authUser, hasAnyPermission]);
  
  // Detect if app is running as PWA
  useEffect(() => {
    // Check if app is running in standalone mode (PWA)
    const isAppInPwaMode = isPwaMode();
    setIsPwaActive(isAppInPwaMode);
    console.log('App is running in PWA mode:', isAppInPwaMode);
  }, []);

  useEffect(() => {
    if (authUser?.company) {
      setCompanyData({
        name: authUser.company.name || '',
        brand_name: authUser.company.brand_name || '',
        address: authUser.company.address || '',
        contact_number: authUser.company.contact_number || '',
        email: authUser.company.email || ''
      });
    }
  }, [authUser]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const handleChangePasswordOpen = () => {
    setOpenChangePassword(true);
    handleClose();
  };

  const handleChangePasswordClose = () => {
    setOpenChangePassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError(null);
    setSuccess(null);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };
  

  const handlePasswordSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setSuccess('Password changed successfully');
      setTimeout(() => {
        handleChangePasswordClose();
      }, 2000);
    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  const handleOpenCompanyDialog = () => {
    setOpenCompanyDialog(true);
    setCompanyError(null);
    setCompanySuccess(null);
  };
  const handleCloseCompanyDialog = () => {
    setOpenCompanyDialog(false);
    setCompanyError(null);
    setCompanySuccess(null);
  };
  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };
  const handleCompanySubmit = async () => {
    if (!authUser?.company) {
      setCompanyError('No company context found.');
      return;
    }
    try {
      setCompanyError(null);
      setCompanySuccess(null);
      await api.put(`/companies/${authUser.company.id}`, companyData);
      setCompanySuccess('Company details updated successfully');
      setTimeout(() => setOpenCompanyDialog(false), 1500);
    } catch (err: any) {
      setCompanyError(err.response?.data?.error || 'Failed to update company details');
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', module: 'dashboard', badge: 0 },
    { text: 'Vendors', icon: <StorefrontIcon />, path: '/vendors', module: 'vendors', badge: 0 },
    { text: 'Purchase Orders', icon: <InventoryIcon />, path: '/purchase-orders', module: 'purchase-orders', badge: 0 },
    { text: 'Purchase', icon: <InventoryIcon />, path: '/materials', module: 'materials', badge: 0 },
    { text: 'Inventory', icon: <BusinessIcon />, path: '/inventory', module: 'inventory', badge: 0 },
    { text: 'Designs', icon: <DesignIcon />, path: '/designs', module: 'designs', badge: 0 },
    { text: 'Production Orders', icon: <FactoryIcon />, path: '/production-orders', module: 'production', badge: 0 },
    { text: 'Chat', icon: <ChatIcon />, path: '/designs/chat', module: 'designs', badge: messagingUnread },
    { text: 'Dupatta Tracker', icon: <CheckroomIcon />, path: '/dupatta-tracker', module: 'dupatta-tracker', badge: 0 },
    { text: 'Job Works', icon: <EngineeringIcon />, path: '/job-works', module: 'job-works', badge: 0 },
    { text: 'Material QC', icon: <QcIcon />, path: '/material-qc', module: 'qc-station', badge: 0 },
  ];

  // Filter menu items based on user permissions
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      if (item.module) {
        const result = hasAnyPermission(item.module);
        return result;
      }
      return true;
    });
  }, [hasAnyPermission]);

  // Production submenu availability and auto-expand on matching route
  const productionSubItems = [
    { text: 'Dashboard', icon: <SpaceDashboardIcon />, path: '/production/finishing-dashboard', module: 'production' },
    { text: 'Opening Entry', icon: <PlaylistAddIcon />, path: '/production/opening-entry', module: 'opening-entry' },
    { text: 'Press Station', icon: <IronIcon />, path: '/production/press', module: 'press-station' },
    { text: 'QC Station', icon: <RateReviewIcon />, path: '/production/qc', module: 'qc-station' },
    { text: 'Repair Queue', icon: <BuildIcon />, path: '/production/repairs', module: 'repair-queue' },
    { text: 'Complete Designs', icon: <CheckCircleIcon />, path: '/production/complete-designs', module: 'production' },
  ];
  
  // Memoize filtered production submenu items based on permissions
  const filteredProductionSubItems = useMemo(() => {
    const filtered = productionSubItems.filter(item => {
      // Strictly check for the specific module permission, no fallback
      const hasPermission = hasAnyPermission(item.module);
      if (item.module === 'opening-entry') {
        console.log(`MainLayout: Opening Entry permission check - module: ${item.module}, hasPermission: ${hasPermission}`);
        console.log(`MainLayout: User permissions:`, permUser?.permissions);
      }
      return hasPermission;
    });
    console.log(`MainLayout: Filtered production submenu items:`, filtered.map(i => i.text));
    return filtered;
  }, [hasAnyPermission, permUser]);
  
  const userHasProduction = filteredProductionSubItems.length > 0;
  
  useEffect(() => {
    if (productionSubItems.some(i => location.pathname.startsWith(i.path))) {
      setProductionOpen(true);
    }
  }, [location.pathname]);

  // Analytics submenu removed

  // Stitching Unit submenu
  const stitchingUnitSubItems = [
    { text: 'Workers', icon: <PeopleIcon />, path: '/workers', module: 'workers' },
    { text: 'Design Cutting', icon: <ContentCutIcon />, path: '/design-cutting', module: 'design-cutting' },
    { text: 'Work Assignments', icon: <WorkIcon />, path: '/assignments', module: 'assignments' },
    { text: 'Payments', icon: <PaymentsIcon />, path: '/payments', module: 'payments' },
    { text: 'Expenses', icon: <ReceiptIcon />, path: '/expenses', module: 'expenses' },
  ];
  const userHasStitchingUnit = stitchingUnitSubItems.some(i => hasAnyPermission(i.module));
  useEffect(() => {
    if (stitchingUnitSubItems.some(i => location.pathname.startsWith(i.path))) {
      setStitchingUnitOpen(true);
    }
  }, [location.pathname]);

  // Reports submenu
  const reportsSubItems = [
    { text: 'Production Summary', icon: <TimelineIcon />, path: '/reports/production-summary', module: 'report-production-summary' },
    { text: 'Worker Performance', icon: <PeopleIcon />, path: '/reports/worker-performance', module: 'report-worker-performance' },
    { text: 'Stitching Pending', icon: <ContentCutIcon />, path: '/reports/stitching-pending', module: 'report-stitching-pending' },
    { text: 'Inventory Consumption', icon: <InventoryIcon />, path: '/reports/inventory-consumption', module: 'report-inventory-consumption' },
    { text: 'Job Work Ageing', icon: <WorkIcon />, path: '/reports/job-work-ageing', module: 'report-job-work-ageing' },
    { text: 'Dispatch & Delivery', icon: <LocalShippingIcon />, path: '/reports/dispatch-delivery', module: 'report-dispatch-delivery' },
    { text: 'Quality Control', icon: <QcIcon />, path: '/reports/quality-control', module: 'report-quality-control' },
    { text: 'Press Productivity', icon: <IronIcon />, path: '/reports/press-productivity', module: 'report-press-productivity' },
    { text: 'Pending PO', icon: <BusinessIcon />, path: '/reports/po-fulfillment', module: 'report-po-fulfillment' },
    { text: 'Pending Barcoding', icon: <QcIcon />, path: '/reports/pending-barcoding', module: 'report-pending-barcoding' },
  ];
  const filteredReportsSubItems = useMemo(() => {
    return reportsSubItems.filter(item => hasAnyPermission(item.module));
  }, [hasAnyPermission]);
  const userHasReports = filteredReportsSubItems.length > 0;
  useEffect(() => {
    if (location.pathname.startsWith('/reports')) {
      setReportsOpen(true);
    }
  }, [location.pathname]);

  // Deprecated modules submenu
  const deprecatedSubItems: typeof menuItems = [];
  const userHasDeprecated = false;

  const drawer = (
    <Box sx={{ mt: { xs: 0, sm: 8 }}}>
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>
                {item.badge > 0 ? (
                  <Badge badgeContent={item.badge} color="error" max={99}>
                    {item.icon}
                  </Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        {userHasStitchingUnit && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setStitchingUnitOpen(prev => !prev)}>
                <ListItemIcon><CheckroomIcon /></ListItemIcon>
                <ListItemText primary="Stitching Unit" />
                {stitchingUnitOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={stitchingUnitOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {stitchingUnitSubItems
                  .filter(i => hasAnyPermission(i.module))
                  .map((sub) => (
                    <ListItem key={sub.text} disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        selected={location.pathname === sub.path}
                        onClick={() => {
                          navigate(sub.path);
                          setMobileOpen(false);
                        }}
                      >
                        <ListItemIcon>{sub.icon}</ListItemIcon>
                        <ListItemText primary={sub.text} />
                      </ListItemButton>
                    </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {userHasProduction && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setProductionOpen(prev => !prev)}>
                <ListItemIcon><FactoryIcon /></ListItemIcon>
                <ListItemText primary="Finishing Unit" />
                {productionOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={productionOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {filteredProductionSubItems.map((sub) => (
                  <ListItem key={sub.text} disablePadding sx={{ pl: 4 }}>
                    <ListItemButton
                      selected={location.pathname === sub.path}
                      onClick={() => {
                        navigate(sub.path);
                        setMobileOpen(false);
                      }}
                    >
                      <ListItemIcon>{sub.icon}</ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Analytics menu removed */}

        {userHasReports && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setReportsOpen(prev => !prev)}>
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Reports" />
                {reportsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {filteredReportsSubItems.map((sub) => (
                  <ListItem key={sub.text} disablePadding sx={{ pl: 4 }}>
                    <ListItemButton
                      selected={location.pathname === sub.path}
                      onClick={() => {
                        navigate(sub.path);
                        setMobileOpen(false);
                      }}
                    >
                      <ListItemIcon>{sub.icon}</ListItemIcon>
                      <ListItemText primary={sub.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Settings */}
        {hasAnyPermission('settings') && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/settings'}
              onClick={() => {
                navigate('/settings');
                setMobileOpen(false);
              }}
            >
              <ListItemIcon><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        )}

        {/* User Management for MASTER users */}
        {authUser?.role === 'MASTER' && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/users'}
              onClick={() => {
                navigate('/users');
                setMobileOpen(false);
              }}
            >
              <ListItemIcon><PeopleIcon /></ListItemIcon>
              <ListItemText primary="User Management" />
            </ListItemButton>
          </ListItem>
        )}

        {/* Role Management for MASTER users */}
        {authUser?.role === 'MASTER' && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/roles'}
              onClick={() => {
                navigate('/roles');
                setMobileOpen(false);
              }}
            >
              <ListItemIcon><RolesIcon /></ListItemIcon>
              <ListItemText primary="Role Management" />
            </ListItemButton>
          </ListItem>
        )}

        {/* Audit Log */}
        {hasAnyPermission('audit-logs') && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/audit-log'}
              onClick={() => {
                navigate('/audit-log');
                setMobileOpen(false);
              }}
            >
              <ListItemIcon><AuditIcon /></ListItemIcon>
              <ListItemText primary="Audit Log" />
            </ListItemButton>
          </ListItem>
        )}

        <Divider sx={{ my: 1 }} />

        {userHasDeprecated && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setDeprecatedOpen(prev => !prev)}>
                <ListItemIcon><ArchiveIcon color="disabled" /></ListItemIcon>
                <ListItemText 
                  primary="Deprecated" 
                  primaryTypographyProps={{ color: 'text.disabled', fontSize: '0.875rem' }}
                />
                {deprecatedOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={deprecatedOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {deprecatedSubItems
                  .filter(i => hasAnyPermission(i.module))
                  .map((sub) => (
                    <ListItem key={sub.text} disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        selected={location.pathname === sub.path}
                        onClick={() => {
                          navigate(sub.path);
                          setMobileOpen(false);
                        }}
                      >
                        <ListItemIcon sx={{ color: 'text.disabled' }}>{sub.icon}</ListItemIcon>
                        <ListItemText 
                          primary={sub.text} 
                          primaryTypographyProps={{ color: 'text.disabled', fontSize: '0.875rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton onClick={() => {
            handleLogout();
            setMobileOpen(false);
          }}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Clear cache functions
  const openClearCacheDialog = () => {
    setClearCacheDialogOpen(true);
  };
  
  const closeClearCacheDialog = () => {
    setClearCacheDialogOpen(false);
  };
  
  const clearCache = async (clearLocalStorage = false) => {
    try {
      setClearingCache(true);
      
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      
      // Clear cache storage
      if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map(key => caches.delete(key)));
      }
      
      // Clear localStorage if requested
      if (clearLocalStorage) {
        localStorage.clear();
      }
      
      // Reload the page
      window.location.reload();
    } catch (error) {
      console.error('Error clearing cache:', error);
      setClearingCache(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      {/* Full-width AppBar that appears at the top */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => navigate('/dashboard')}
          >
            StitchFlo
          </Typography>
          
          {/* PWA Mode Indicator */}
          {isPwaActive && (
            <Chip
              label="App Mode"
              color="secondary"
              size="small"
              sx={{ 
                mr: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.7rem'
              }}
            />
          )}
          
          {/* Clear Cache Button */}
          <Tooltip title="Clear app cache (fixes loading issues)">
            <IconButton 
              color="inherit" 
              onClick={openClearCacheDialog}
              size="medium"
              sx={{ mr: 1 }}
            >
              <CachedIcon />
            </IconButton>
          </Tooltip>
          
          {authUser && (
            <>
              <NotificationBell />
              <Button
                color="inherit"
                onClick={handleMenu}
                startIcon={<Avatar sx={{ width: 32, height: 32 }}>{authUser.name[0]}</Avatar>}
              >
                {authUser.name}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleChangePasswordOpen}>Change Password</MenuItem>
                {authUser?.role?.toLowerCase() === 'master' && (
                  <MenuItem onClick={handleOpenCompanyDialog}>Change Company Details</MenuItem>
                )}
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Main content area with sidebar and content */}
      <Box sx={{ display: 'flex', flex: 1, pt: { xs: 7, sm: 8 } }}>
        {/* Sidebar */}
        <Box
          component="nav"
          sx={{ 
            width: { sm: drawerWidth }, 
            flexShrink: { sm: 0 }
          }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                borderRight: '1px solid',
                borderColor: 'divider',
                overflowY: 'auto',
                top: 56,
                height: 'calc(100% - 56px)',
              },
            }}
          >
            {drawer}
          </Drawer>
          
          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            width: '100%',
            minHeight: '100vh',
            bgcolor: 'background.default',
            overflowX: 'hidden'
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={openChangePassword} onClose={handleChangePasswordClose}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            name="currentPassword"
            label="Current Password"
            type="password"
            fullWidth
            value={passwordData.currentPassword}
            onChange={handlePasswordChange}
          />
          <TextField
            margin="dense"
            name="newPassword"
            label="New Password"
            type="password"
            fullWidth
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
          />
          <TextField
            margin="dense"
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            fullWidth
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangePasswordClose}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} variant="contained" color="primary">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Cache Dialog */}
      <Dialog open={clearCacheDialogOpen} onClose={closeClearCacheDialog}>
        <DialogTitle>Clear Application Cache</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Clearing the cache may help resolve display issues or loading outdated content.
          </Typography>
          <Typography paragraph>
            You have two options:
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Standard Clear:</strong> Clears the application cache, but keeps your login session.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Full Clear:</strong> Clears all data including your login session (you'll need to log in again).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearCacheDialog} disabled={clearingCache}>
            Cancel
          </Button>
          <Button 
            onClick={() => clearCache(false)}
            color="primary"
            disabled={clearingCache}
          >
            {clearingCache ? <CircularProgress size={24} /> : 'Standard Clear'}
          </Button>
          <Button 
            onClick={() => clearCache(true)}
            color="error"
            disabled={clearingCache}
          >
            {clearingCache ? <CircularProgress size={24} /> : 'Full Clear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company Details Dialog */}
      <Dialog open={openCompanyDialog} onClose={handleCloseCompanyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Change Company Details</DialogTitle>
        <DialogContent>
          {companyError && <Alert severity="error" sx={{ mb: 2 }}>{companyError}</Alert>}
          {companySuccess && <Alert severity="success" sx={{ mb: 2 }}>{companySuccess}</Alert>}
          <TextField
            margin="dense"
            name="name"
            label="Company Name"
            fullWidth
            value={companyData.name}
            onChange={handleCompanyChange}
          />
          <TextField
            margin="dense"
            name="brand_name"
            label="Brand Name"
            fullWidth
            value={companyData.brand_name}
            onChange={handleCompanyChange}
          />
          <TextField
            margin="dense"
            name="address"
            label="Address"
            fullWidth
            value={companyData.address}
            onChange={handleCompanyChange}
          />
          <TextField
            margin="dense"
            name="contact_number"
            label="Contact Number"
            fullWidth
            value={companyData.contact_number}
            onChange={handleCompanyChange}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            fullWidth
            value={companyData.email}
            onChange={handleCompanyChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompanyDialog}>Cancel</Button>
          <Button onClick={handleCompanySubmit} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 